import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { websiteService } from '../services/api';
import { Website } from '../types';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import OnlineUsers from '../components/OnlineUsers';

const Dashboard: React.FC = () => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        setLoading(true);
        const data = await websiteService.getAll();
        setWebsites(data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des sites web:', err);
        setError('Impossible de charger les données. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchWebsites();
  }, []);

  // Calculer les statistiques
  const activeWebsites = websites.filter(site => site.status === 'active').length;
  const dormantWebsites = websites.filter(site => site.status === 'dormant').length;
  const terminatedWebsites = websites.filter(site => site.status === 'terminated').length;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Tableau de bord</h1>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {/* Statistiques des sites */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold mb-2">Total des sites</h2>
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{websites.length}</p>
                  <Link to="/sites" className="text-blue-500 hover:underline mt-2 inline-block">
                    Voir tous les sites
                  </Link>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold mb-2">Contrats actifs</h2>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">{activeWebsites}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold mb-2">Contrats en sommeil</h2>
                  <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{dormantWebsites}</p>
                </div>
              </div>
            </div>

            {/* Utilisateurs connectés */}
            <div className="lg:col-span-1">
              <OnlineUsers />
            </div>
          </div>

          {/* Ligne supplémentaire pour les contrats terminés */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-2">Contrats terminés</h2>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400">{terminatedWebsites}</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-4">Rapports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <ChartBarIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400 mr-3" />
                <h3 className="text-xl font-semibold">Rapport MainWP</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Vérifiez les sites présents dans MainWP mais non référencés dans la base de données.
              </p>
              <Link 
                to="/reports/mainwp" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Voir le rapport
              </Link>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <svg 
                  className="h-8 w-8 text-orange-600 dark:text-orange-400 mr-3" 
                  viewBox="0 0 256 246" 
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                >
                  <path d="M128.001 0C57.317 0 0 57.307 0 128.001c0 70.693 57.317 128 128.001 128 70.691 0 127.999-57.307 127.999-128C256 57.307 198.692 0 128.001 0zm0 219.327c-50.146 0-91.327-41.18-91.327-91.327 0-50.146 41.181-91.328 91.327-91.328 50.147 0 91.328 41.182 91.328 91.328 0 50.147-41.181 91.327-91.328 91.327zm0-167.744c-42.165 0-76.417 34.252-76.417 76.417 0 42.165 34.252 76.417 76.417 76.417 42.166 0 76.418-34.252 76.418-76.417 0-42.165-34.252-76.417-76.418-76.417zm0 134.398c-31.986 0-57.981-25.994-57.981-57.981 0-31.987 25.995-57.981 57.981-57.981 31.987 0 57.982 25.994 57.982 57.981 0 31.987-25.995 57.981-57.982 57.981z" />
                </svg>
                <h3 className="text-xl font-semibold">Rapport Minio</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Analysez les buckets Minio et identifiez les problèmes de configuration.
              </p>
              <Link 
                to="/reports/minio" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                Voir le rapport
              </Link>
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-4">Sites récemment ajoutés</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            {websites.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Client
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        URL
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Plateforme
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        État du contrat
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {websites.slice(0, 5).map((website) => (
                      <tr key={website.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{website.clientName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a 
                            href={website.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {website.url}
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-300">{website.platform}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            website.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                            website.status === 'dormant' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                            website.status === 'terminated' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}>
                            {website.status === 'active' ? 'Actif' : 
                             website.status === 'dormant' ? 'En sommeil' : 
                             website.status === 'terminated' ? 'Terminé' :
                             website.status || 'Non spécifié'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link to={`/sites/${website.id}`} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4">
                            Détails
                          </Link>
                          <Link to={`/sites/${website.id}/edit`} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300">
                            Modifier
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center">
                <p className="text-gray-500 dark:text-gray-400">Aucun site web enregistré.</p>
                <Link to="/sites/new" className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                  Ajouter un site
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard; 